/**
 * One session, rendered as an ordered timeline.
 *
 * Shared by the standalone Session Traces page and the User Detail → Sessions
 * tab, so both read a session the same way.
 *
 * Dwell bars are scaled to the longest dwell *in this session*, not a global
 * maximum — the question being answered is "where did this sitting stall",
 * which is relative.
 *
 * Each row is annotated with what it was showing — the question's subject and
 * module, the paper's title — by classifying its path (`pageRefs.ts`) and
 * resolving every id on the timeline in one request.
 *
 * See docs/FeatureDesigns/user-actions.md.
 */

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchWithAdmin } from '../../api/fetchHelper';
import type { QuestionRefInfo, ResolvedRefs, SessionSummary, UserActionEvent } from './types';
import { formatClock, formatDwell } from './format';
import { classifyPage, refKey, type PageClass, type PageRef } from './pageRefs';

interface SessionDetailResponse {
  session: SessionSummary | null;
  events: UserActionEvent[];
}

export interface SessionTimelineProps {
  userId: string;
  sessionId: string;
}

/**
 * An event's label in the legacy shape. The activity stream stores one
 * `target` string — `page`, `page#screen` for a screen, `page:control` for a
 * click — where the legacy store kept `page` / `screen` / `props.target`.
 */
function eventLabel(e: UserActionEvent): { page: string; screen?: string; control?: string } {
  if (e.page) {
    const control = e.props?.target;
    return { page: e.page, screen: e.screen, control: control === undefined ? undefined : String(control) };
  }
  const target = e.target ?? '';
  if (e.kind === 'screen_view') {
    const at = target.lastIndexOf('#');
    if (at > 0) return { page: target.slice(0, at), screen: target.slice(at + 1) };
  }
  // A click's `page:control` cannot be split safely — page labels carry
  // `:id` — so it is shown whole, as the control.
  if (e.kind === 'click') return { page: '', control: target };
  return { page: target };
}

/** Where the row actually was: the raw path when recorded, else the label. */
const rawPath = (e: UserActionEvent): string => e.path ?? eventLabel(e).page;

function questionText(q: QuestionRefInfo): string {
  return [q.subject, q.modulePath].filter(Boolean).join(' · ');
}

/** One resolved ref, as a short phrase with a link into the console. */
function RefChip({ r, refs }: { r: PageRef; refs: ResolvedRefs | undefined }) {
  switch (r.type) {
    case 'question': {
      const q = refs?.question[r.id];
      return (
        <Link className="ua-ref-link" to={`/questions?id=${encodeURIComponent(r.id)}`} title={q?.snippet ?? r.id}>
          {q ? questionText(q) : 'question'}
        </Link>
      );
    }
    case 'questionSet': {
      const qs = refs?.questionSet[r.id];
      return (
        <Link className="ua-ref-link" to={`/assignments-tests/${encodeURIComponent(r.id)}`} title={r.id}>
          {qs ? qs.title : 'paper'}
        </Link>
      );
    }
    case 'session': {
      const s = refs?.session[r.id];
      return s ? (
        <Link className="ua-ref-link" to={`/assignments-tests/${encodeURIComponent(s.questionSetId)}`} title={r.id}>
          {s.questionSetTitle}
        </Link>
      ) : <span className="ua-ref-dim" title={r.id}>sitting</span>;
    }
    case 'attempt': {
      const a = refs?.attempt[r.id];
      return (
        <>
          <Link className="ua-ref-link" to={`/submissions/${encodeURIComponent(r.id)}`} title={r.id}>attempt</Link>
          {a?.question && (
            <Link className="ua-ref-link" to={`/questions?id=${encodeURIComponent(a.questionId)}`} title={a.question.snippet ?? a.questionId}>
              {questionText(a.question)}
            </Link>
          )}
        </>
      );
    }
    case 'user': {
      const u = refs?.user[r.id];
      return (
        <Link className="ua-ref-link" to={`/users/${encodeURIComponent(r.id)}`} title={u?.email ?? r.id}>
          {u?.name || u?.email || 'student'}
        </Link>
      );
    }
  }
}

function RefAnnotation({ cls, refs }: { cls: PageClass; refs: ResolvedRefs | undefined }) {
  return (
    <span className="ua-ref">
      <span className="ua-ref-cat">{cls.category}</span>
      {cls.refs.map(r => <RefChip key={refKey(r)} r={r} refs={refs} />)}
    </span>
  );
}

export function SessionTimeline({ userId, sessionId }: SessionTimelineProps) {
  const detail = useQuery({
    queryKey: ['user-action-session', userId, sessionId],
    queryFn: () => fetchWithAdmin<SessionDetailResponse>(
      `/console/user-actions/${sessionId}?userId=${encodeURIComponent(userId)}`,
    ),
  });

  // Classify every row once; the ids across the whole timeline are resolved
  // in a single request.
  // Ordered by time, not by `seq`. Before the recorder persisted its counters,
  // a reload resumed the sitting with `seq` back at 0, so sittings recorded then
  // carry repeated seqs that interleave two page loads. Time is the true order;
  // `seq` only breaks ties.
  const events = useMemo(
    () => detail.data?.events
      ? [...detail.data.events].sort((a, b) => Date.parse(a.t) - Date.parse(b.t) || a.seq - b.seq)
      : undefined,
    [detail.data],
  );
  // By position — `seq` is not unique in those sittings.
  const classes = useMemo(
    () => (events ?? []).map(e => (e.kind === 'click' ? null : classifyPage(rawPath(e)))),
    [events],
  );
  const allRefs = useMemo(() => {
    const byKey = new Map<string, PageRef>();
    for (const c of classes) for (const r of c?.refs ?? []) byKey.set(refKey(r), r);
    return [...byKey.values()];
  }, [classes]);

  const resolved = useQuery({
    queryKey: ['user-action-refs', userId, sessionId, allRefs.map(refKey).join('|')],
    queryFn: () => fetchWithAdmin<ResolvedRefs>('/console/user-action-refs', {
      method: 'POST',
      body: JSON.stringify({ userId, refs: allRefs }),
    }),
    enabled: allRefs.length > 0,
    staleTime: 5 * 60_000,
  });

  if (detail.isLoading) return <p className="ua-muted">Loading timeline…</p>;
  if (detail.isError) return <p className="ua-error">Failed: {(detail.error as Error).message}</p>;

  const session = detail.data?.session ?? null;

  if (!events || events.length === 0) {
    return <p className="ua-muted">No events recorded for this session.</p>;
  }

  // The header can expire before its event docs, so fall back to the first
  // event rather than rendering every offset as NaN.
  const startedAt = session?.startedAt ?? events[0].t;
  const startMs = Date.parse(startedAt);
  const maxDwell = Math.max(...events.map(e => e.dwellMs ?? 0), 1);

  return (
    <div className="ua-timeline">
      {session?.truncated && (
        <p className="ua-warn">
          Some batches exceeded the size cap — this timeline is missing events.
        </p>
      )}
      <ol className="ua-events">
        {events.map((e, i) => {
          const offset = Date.parse(e.t) - startMs;
          const isClick = e.kind === 'click';
          const label = eventLabel(e);
          const cls = classes[i];
          return (
            <li key={i} className={`ua-event ua-event--${e.kind}`}>
              <span className="ua-offset">{formatClock(offset)}</span>
              <span className={`ua-kind ua-kind--${e.kind}`}>
                {e.kind === 'page_view' ? 'page' : e.kind === 'screen_view' ? 'screen' : 'click'}
              </span>
              <span className="ua-label">
                {label.screen ? (
                  <>
                    <span className="ua-page-dim">{label.page}</span>
                    <span className="ua-screen">{label.screen}</span>
                  </>
                ) : label.page ? (
                  <span className="ua-page">{label.page}</span>
                ) : null}
                {label.control !== undefined && (
                  <span className="ua-target">{label.control}</span>
                )}
                {e.path && e.path !== label.page && (
                  <span className="ua-path" title={e.path}>{e.path}</span>
                )}
                {cls && <RefAnnotation cls={cls} refs={resolved.data} />}
              </span>
              <span className="ua-dwell">
                {isClick ? (
                  <span className="ua-dwell-na">—</span>
                ) : e.dwellMs === undefined ? (
                  // Only the trailing event of a live session has no dwell yet.
                  <span className="ua-dwell-open">open</span>
                ) : (
                  <>
                    <span className="ua-dwell-bar" style={{ width: `${(e.dwellMs / maxDwell) * 100}%` }} />
                    <span className="ua-dwell-text">{formatDwell(e.dwellMs)}</span>
                  </>
                )}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
